import React, { useState, useEffect, useRef } from 'react';
import {
  Share2,
  Copy,
  Check,
  MessageCircle,
  ExternalLink,
  ChevronDown,
  X,
  Send,
} from 'lucide-react';
import { Job } from '../../types';

export interface ShareJobButtonProps {
  job: Job;
  variant?: 'primary' | 'secondary' | 'outline' | 'compact' | 'icon';
  className?: string;
  customUrl?: string;
  showDropdown?: boolean;
}

export const ShareJobButton: React.FC<ShareJobButtonProps> = ({
  job,
  variant = 'outline',
  className = '',
  customUrl,
  showDropdown = true,
}) => {
  const [copied, setCopied] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [supportsNativeShare, setSupportsNativeShare] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const toastTimeoutRef = useRef<any>(null);

  // Check if Web Share API is supported on the client
  useEffect(() => {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      setSupportsNativeShare(true);
    }
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const showToast = (message: string) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToastMessage(message);
    toastTimeoutRef.current = setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  const getCanonicalJobUrl = () => {
    if (customUrl) return customUrl;
    if (typeof window === 'undefined') return '';
    const origin = window.location.origin;
    return `${origin}/job?id=${encodeURIComponent(job.id)}`;
  };

  const jobUrl = getCanonicalJobUrl();

  const shareTitle = `${job.title} | JobMatch NG`;
  const shareText = `Check out this job on JobMatch NG: "${job.title}" in ${
    job.cityArea ? `${job.cityArea}, ` : ''
  }${job.lga || ''}, ${job.state || 'Nigeria'}. Budget: ₦${Number(
    job.budgetEstimate || 0
  ).toLocaleString()}. Verified artisans can review details and submit quotes:`;

  const copyToClipboard = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(jobUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = jobUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      showToast('Job link copied to clipboard! You can paste it into WhatsApp, SMS, or any app.');
      setTimeout(() => setCopied(false), 3000);
    } catch (_err) {
      showToast('Please copy this link: ' + jobUrl);
    }
  };

  const handleNativeShare = async () => {
    if (supportsNativeShare) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: jobUrl,
        });
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          // Fall back to copy link if native share encounters an error
          await copyToClipboard();
        }
      }
    } else {
      // Native share not supported: directly fallback to copy link
      await copyToClipboard();
    }
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // On supported devices, trigger Web Share API directly
    if (supportsNativeShare) {
      handleNativeShare();
    } else {
      // On desktop / unsupported browsers, copy link directly
      copyToClipboard();
    }
  };

  const handleWhatsAppShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
      `${shareText} ${jobUrl}`
    )}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    setIsMenuOpen(false);
  };

  const handleTelegramShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(
      jobUrl
    )}&text=${encodeURIComponent(shareText)}`;
    window.open(telegramUrl, '_blank', 'noopener,noreferrer');
    setIsMenuOpen(false);
  };

  // Base button styles per variant
  const getButtonStyles = () => {
    switch (variant) {
      case 'primary':
        return 'bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm';
      case 'secondary':
        return 'bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 text-white font-bold shadow-sm';
      case 'compact':
        return 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold';
      case 'icon':
        return 'p-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors';
      case 'outline':
      default:
        return 'bg-white hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 font-bold shadow-xs';
    }
  };

  if (variant === 'icon') {
    return (
      <div className="relative inline-block" ref={menuRef}>
        <button
          type="button"
          onClick={handleShareClick}
          title={copied ? 'Link Copied!' : supportsNativeShare ? 'Share Job' : 'Copy Job Link'}
          aria-label="Share Job"
          className={`${getButtonStyles()} min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer ${className}`}
        >
          {copied ? (
            <Check className="w-4 h-4 text-emerald-600" />
          ) : (
            <Share2 className="w-4 h-4" />
          )}
        </button>

        {/* Global Toast */}
        {toastMessage && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-70 bg-slate-950 text-white px-4 py-3 rounded-2xl shadow-2xl border border-slate-800 flex items-center gap-3 text-xs max-w-sm animate-fade-in">
            <Check className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="flex-1 font-medium">{toastMessage}</span>
            <button
              onClick={() => setToastMessage(null)}
              className="text-slate-400 hover:text-white p-1"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative inline-flex items-center" ref={menuRef}>
      {/* Main Share Button */}
      <div className="inline-flex rounded-xl shadow-xs overflow-hidden">
        <button
          type="button"
          onClick={handleShareClick}
          className={`${getButtonStyles()} px-4 py-2 text-xs flex items-center gap-2 transition-all cursor-pointer min-h-[44px] ${
            showDropdown ? 'rounded-l-xl rounded-r-none border-r-0' : 'rounded-xl'
          } ${className}`}
          title={supportsNativeShare ? 'Share this job with WhatsApp or other apps' : 'Copy job link to clipboard'}
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-emerald-700 dark:text-emerald-300">Link Copied!</span>
            </>
          ) : (
            <>
              <Share2 className="w-4 h-4" />
              <span>{supportsNativeShare ? 'Share Job' : 'Share / Copy Link'}</span>
            </>
          )}
        </button>

        {/* Dropdown Options Toggle */}
        {showDropdown && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen((prev) => !prev);
            }}
            aria-label="More share options"
            className={`${getButtonStyles()} px-2 py-2 text-xs transition-all cursor-pointer min-h-[44px] border-l border-slate-200/60 dark:border-slate-700/60 rounded-r-xl rounded-l-none`}
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {/* Share Options Dropdown Menu */}
      {isMenuOpen && (
        <div className="absolute top-full right-0 mt-1.5 w-60 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl py-1.5 z-60 animate-fade-in text-xs text-slate-700 dark:text-slate-200">
          <div className="px-3 py-1.5 text-[10px] uppercase font-bold text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-800">
            Share Job via
          </div>

          {/* WhatsApp Direct Option */}
          <button
            type="button"
            onClick={handleWhatsAppShare}
            className="w-full px-3.5 py-2.5 text-left flex items-center gap-2.5 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 text-slate-800 dark:text-slate-200 hover:text-emerald-700 dark:hover:text-emerald-400 transition-colors cursor-pointer"
          >
            <div className="w-6 h-6 rounded-lg bg-emerald-100 dark:bg-emerald-900/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
              <MessageCircle className="w-3.5 h-3.5" />
            </div>
            <div>
              <span className="font-bold block">Share to WhatsApp</span>
              <span className="text-[10px] text-slate-400 block">Send to group or contact</span>
            </div>
          </button>

          {/* Telegram Option */}
          <button
            type="button"
            onClick={handleTelegramShare}
            className="w-full px-3.5 py-2.5 text-left flex items-center gap-2.5 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-slate-800 dark:text-slate-200 hover:text-blue-700 dark:hover:text-blue-400 transition-colors cursor-pointer"
          >
            <div className="w-6 h-6 rounded-lg bg-blue-100 dark:bg-blue-900/60 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
              <Send className="w-3.5 h-3.5" />
            </div>
            <div>
              <span className="font-bold block">Share to Telegram</span>
              <span className="text-[10px] text-slate-400 block">Send via Telegram chat</span>
            </div>
          </button>

          {/* Copy Link Option */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard();
              setIsMenuOpen(false);
            }}
            className="w-full px-3.5 py-2.5 text-left flex items-center gap-2.5 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-200 transition-colors cursor-pointer border-t border-slate-100 dark:border-slate-800"
          >
            <div className="w-6 h-6 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center shrink-0">
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            </div>
            <div>
              <span className="font-bold block">{copied ? 'Link Copied!' : 'Copy Job Link'}</span>
              <span className="text-[10px] text-slate-400 block">Copy link to clipboard</span>
            </div>
          </button>

          {/* Native Web Share API trigger if supported */}
          {supportsNativeShare && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(false);
                handleNativeShare();
              }}
              className="w-full px-3.5 py-2 text-left flex items-center gap-2.5 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 text-[11px] font-semibold transition-colors cursor-pointer border-t border-slate-100 dark:border-slate-800"
            >
              <Share2 className="w-3.5 h-3.5 text-slate-400" />
              <span>More device apps...</span>
            </button>
          )}
        </div>
      )}

      {/* Global Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-70 bg-slate-950 text-white px-4 py-3 rounded-2xl shadow-2xl border border-slate-800 flex items-center gap-3 text-xs max-w-sm animate-fade-in">
          <Check className="w-4 h-4 text-emerald-400 shrink-0" />
          <span className="flex-1 font-medium">{toastMessage}</span>
          <button
            onClick={() => setToastMessage(null)}
            className="text-slate-400 hover:text-white p-1 cursor-pointer"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};
